import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { ArrowSmallRight, VyIcon } from '@vy/ui-design-system';
import type { IntegrationProviders } from '@vy/types';
import { INTEGRATION_PROVIDERS } from '@vy/types';
import React from 'react';
import { useMenu } from '../../../../hooks/use-menu';

type SyncCreditNoteMenuProps = {
  connectedIntegrations: IntegrationProviders[];
  handleMenuClose: () => void;
  onSyncToNetSuite?: () => void;
  onSyncToXero?: () => void;
  onSyncToQuickBooks?: () => void;
  isSyncDisabled?: boolean;
  isNetSuiteSyncDisabled?: boolean;
  isXeroSyncDisabled?: boolean;
  isQuickBooksSyncDisabled?: boolean;
};

export const SyncCreditNoteMenu: React.FC<SyncCreditNoteMenuProps> = ({
  connectedIntegrations,
  handleMenuClose,
  onSyncToNetSuite,
  onSyncToXero,
  onSyncToQuickBooks,
  isSyncDisabled,
  isNetSuiteSyncDisabled,
  isXeroSyncDisabled,
  isQuickBooksSyncDisabled,
}) => {
  const {
    isOpen: isSyncMenuOpen,
    handleMenuOpen: handleSyncMenuOpen,
    handleMenuClose: handleSyncMenuClose,
    anchorElement: syncMenuAnchorElement,
  } = useMenu();

  const isNetSuiteConnected = connectedIntegrations.includes(INTEGRATION_PROVIDERS.netSuite);
  const isXeroConnected = connectedIntegrations.includes(INTEGRATION_PROVIDERS.xero);
  const isQuickBooksConnected = connectedIntegrations.includes(INTEGRATION_PROVIDERS.quickBooks);

  const handleMenuItemClick = (action?: () => void) => {
    return () => {
      handleSyncMenuClose();
      handleMenuClose();
      action?.();
    };
  };

  return (
    <>
      <MenuItem onClick={handleSyncMenuOpen} disabled={isSyncDisabled} data-vy="credit-note-dot-menu-sync">
        <ListItemIcon>
          <VyIcon icon="Synchronize" color="black" style={{ transform: 'scale(0.7)' }} />
        </ListItemIcon>
        <ListItemText>Sync</ListItemText>
        <ArrowSmallRight />
      </MenuItem>

      <Menu
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        open={isSyncMenuOpen}
        anchorEl={syncMenuAnchorElement}
        onClose={handleSyncMenuClose}
      >
        {isNetSuiteConnected && (
          <MenuItem
            onClick={handleMenuItemClick(onSyncToNetSuite)}
            disabled={isNetSuiteSyncDisabled}
            data-vy="credit-note-sync-to-netsuite"
          >
            Sync to NetSuite
          </MenuItem>
        )}

        {isXeroConnected && (
          <MenuItem
            onClick={handleMenuItemClick(onSyncToXero)}
            disabled={isXeroSyncDisabled}
            data-vy="credit-note-sync-to-xero"
          >
            Sync to Xero
          </MenuItem>
        )}

        {isQuickBooksConnected && (
          <MenuItem
            onClick={handleMenuItemClick(onSyncToQuickBooks)}
            disabled={isQuickBooksSyncDisabled}
            data-vy="credit-note-sync-to-quickbooks"
          >
            Sync to QuickBooks
          </MenuItem>
        )}
      </Menu>
    </>
  );
};
